import { useState } from 'react';
import { T, Var, useGT, useMessages } from 'gt-react';
import {
  ArrowsClockwiseIcon,
  CheckCircleIcon,
  LinkBreakIcon,
  LinkIcon,
  NoteIcon,
  NotePencilIcon,
  PencilSimpleIcon,
  ReceiptIcon,
  StackPlusIcon,
  TrashIcon,
  UserIcon,
  type Icon,
} from '@phosphor-icons/react';
import type { CashReceiptActivity } from '@erp/rpc';
import { Action, Subject } from '@erp/authz';
import { useCan } from '@/hooks/useCan';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { describeCashReceiptActivity } from './cashReceiptActivityLabels';
import {
  ActivityRail,
  ActivityWhen,
  type ActivityTone,
} from '@/components/common/activity-timeline/ActivityTimelinePrimitives';
import {
  useArCashReceiptActivity,
  useAddCashReceiptNote,
  useEditCashReceiptNote,
  useDeleteCashReceiptNote,
} from '@/api/rpc/hooks/accounts-receivable/useArCashReceipts';

const NOTE_KIND = 'cash_receipt.note_added';

/**
 * Kind → tone and icon, as lookup maps rather than switches, matching
 * `GroupActivityTimeline`'s KIND_ICONS. Both fall back for a kind written by
 * a newer backend than this bundle.
 */
const KIND_TONES: Record<string, ActivityTone> = {
  [NOTE_KIND]: 'note',
  'cash_receipt.payment_linked': 'green',
  'cash_receipt.remittance_linked': 'green',
  'cash_receipt.customer_changed': 'purple',
  'cash_receipt.payment_unlinked': 'amber',
  'cash_receipt.details_edited': 'amber',
};

const KIND_ICONS: Record<string, Icon> = {
  [NOTE_KIND]: NoteIcon,
  'cash_receipt.created': NotePencilIcon,
  'cash_receipt.ingested': ReceiptIcon,
  'cash_receipt.customer_changed': UserIcon,
  'cash_receipt.payment_linked': LinkIcon,
  'cash_receipt.remittance_linked': CheckCircleIcon,
  'cash_receipt.payment_unlinked': LinkBreakIcon,
  'cash_receipt.details_edited': PencilSimpleIcon,
  'cash_receipt.consolidated': StackPlusIcon,
};

function toneFor(kind: string): ActivityTone {
  return KIND_TONES[kind] ?? 'stone';
}

function iconFor(kind: string): React.ReactNode {
  const IconCmp = KIND_ICONS[kind] ?? ArrowsClockwiseIcon;
  return <IconCmp className="h-3.5 w-3.5" />;
}

interface NoteRowProps {
  activity: CashReceiptActivity;
  isLast: boolean;
  canUpdate: boolean;
  editPending: boolean;
  deletePending: boolean;
  onEdit: (activityId: string, body: string) => Promise<void>;
  onDelete: (activityId: string) => Promise<void>;
}

function NoteRow({
  activity,
  isLast,
  canUpdate,
  editPending,
  deletePending,
  onEdit,
  onDelete,
}: NoteRowProps) {
  const gt = useGT();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(activity.body ?? '');

  const edited = activity.updatedAt !== activity.occurredAt;

  async function handleSave() {
    const body = draft.trim();
    if (body.length === 0 || body === activity.body) {
      setIsEditing(false);
      return;
    }
    try {
      await onEdit(activity.id, body);
      setIsEditing(false);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : gt('Could not save the note.', {
              context: 'toast, editing a cash receipt note failed',
            }),
      );
    }
  }

  async function handleDelete() {
    try {
      await onDelete(activity.id);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : gt('Could not delete the note.', {
              context: 'toast, deleting a cash receipt note failed',
            }),
      );
    }
  }

  return (
    <ActivityRail
      tone="note"
      icon={<NoteIcon className="h-3.5 w-3.5" />}
      isLast={isLast}
    >
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-xs font-medium text-muted-foreground">
          {activity.actorName ? (
            <T context="cash receipt activity feed, note left by a named user">
              Note by <Var>{activity.actorName}</Var>
            </T>
          ) : (
            <T context="cash receipt activity feed, note with no known author">
              Note
            </T>
          )}
        </span>
        <ActivityWhen at={activity.occurredAt} />
        {edited && (
          <span className="text-xs text-muted-foreground">
            <T context="cash receipt activity feed, marks a note that was edited after posting">
              (edited)
            </T>
          </span>
        )}
      </div>

      {isEditing ? (
        <div className="mt-1 space-y-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            className="text-sm"
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => void handleSave()}
              disabled={editPending || draft.trim().length === 0}
            >
              <T context="button, save an edited cash receipt note">Save</T>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setDraft(activity.body ?? '');
                setIsEditing(false);
              }}
              disabled={editPending}
            >
              <T context="button, cancel editing a cash receipt note">Cancel</T>
            </Button>
          </div>
        </div>
      ) : (
        <div className="group/note flex items-start gap-2">
          <div className="mt-1 flex-1 text-sm text-foreground whitespace-pre-wrap rounded-md bg-amber-50 border-l-2 border-amber-300 px-3 py-1.5">
            {activity.body}
          </div>
          {activity.editable && canUpdate && (
            // `editable` is authorship; the permission is separately required
            // by the handler, so gate on both. Revealed on hover to keep the
            // feed quiet, but focus-visible keeps them reachable from the
            // keyboard.
            <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover/note:opacity-100 focus-within:opacity-100">
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6"
                onClick={() => setIsEditing(true)}
                aria-label={gt('Edit note', {
                  context: 'accessible label, edit a cash receipt note',
                })}
              >
                <PencilSimpleIcon className="h-3 w-3" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 text-destructive hover:text-destructive"
                onClick={() => void handleDelete()}
                disabled={deletePending}
                aria-label={gt('Delete note', {
                  context: 'accessible label, delete a cash receipt note',
                })}
              >
                <TrashIcon className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      )}
    </ActivityRail>
  );
}

function LifecycleRow({
  activity,
  isLast,
}: {
  activity: CashReceiptActivity;
  isLast: boolean;
}) {
  const m = useMessages();
  return (
    <ActivityRail
      tone={toneFor(activity.kind)}
      icon={iconFor(activity.kind)}
      isLast={isLast}
    >
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-sm text-foreground font-medium">
          {describeCashReceiptActivity(activity, m)}
        </span>
        {activity.actorName && (
          <span className="text-xs text-muted-foreground">
            <T context="cash receipt activity feed, who performed an action">
              by <Var>{activity.actorName}</Var>
            </T>
          </span>
        )}
        <ActivityWhen at={activity.occurredAt} />
      </div>
    </ActivityRail>
  );
}

function NoteComposer({
  addPending,
  onAdd,
}: {
  addPending: boolean;
  onAdd: (body: string) => Promise<void>;
}) {
  const gt = useGT();
  const [draft, setDraft] = useState('');

  async function handleSubmit() {
    const body = draft.trim();
    if (body.length === 0) return;
    try {
      await onAdd(body);
      setDraft('');
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : gt('Could not add the note.', {
              context: 'toast, adding a cash receipt note failed',
            }),
      );
    }
  }

  return (
    <div className="space-y-2">
      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={2}
        className="text-sm"
        placeholder={gt('Leave a note on this receipt...', {
          context: 'placeholder, cash receipt note input',
        })}
      />
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={() => void handleSubmit()}
          disabled={addPending || draft.trim().length === 0}
        >
          <T context="button, post a note on a cash receipt">Add Note</T>
        </Button>
      </div>
    </div>
  );
}

export function CashReceiptActivityFeed({
  activities,
  isLoading,
  isError,
  canUpdate,
  addPending,
  editPending,
  deletePending,
  onAdd,
  onEdit,
  onDelete,
}: {
  activities: CashReceiptActivity[];
  isLoading: boolean;
  isError: boolean;
  canUpdate: boolean;
  addPending: boolean;
  editPending: boolean;
  deletePending: boolean;
  onAdd: (body: string) => Promise<void>;
  onEdit: (activityId: string, body: string) => Promise<void>;
  onDelete: (activityId: string) => Promise<void>;
}) {
  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <T context="AR cash receipt, section heading for the activity timeline">
          Activity
        </T>
      </p>

      {/* Adding a note needs the same permission the handler enforces, so a
          read-only viewer isn't offered an action the server will refuse.
          Keyed by receipt so switching receipts never carries a draft over. */}
      {canUpdate && <NoteComposer addPending={addPending} onAdd={onAdd} />}

      {isLoading && (
        <p className="text-sm text-muted-foreground">
          <T context="loading state, cash receipt activity feed">Loading...</T>
        </p>
      )}
      {isError && (
        <p className="text-sm text-destructive">
          <T context="error state, cash receipt activity feed">
            Could not load activity.
          </T>
        </p>
      )}
      {!isLoading && !isError && activities.length === 0 && (
        <p className="text-sm text-muted-foreground">
          <T context="empty state, cash receipt activity feed">
            No activity yet.
          </T>
        </p>
      )}

      {activities.length > 0 && (
        <ul>
          {activities.map((activity, index) => {
            const isLast = index === activities.length - 1;
            return activity.kind === NOTE_KIND ? (
              <NoteRow
                key={activity.id}
                activity={activity}
                isLast={isLast}
                canUpdate={canUpdate}
                editPending={editPending}
                deletePending={deletePending}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ) : (
              <LifecycleRow
                key={activity.id}
                activity={activity}
                isLast={isLast}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * The receipt's activity feed: what happened to it, and what people said about
 * it. Notes are editable by whoever left them; everything else is an
 * immutable record and carries no affordances.
 */
export function CashReceiptActivitySection({
  receiptId,
}: {
  receiptId: string;
}) {
  const [canUpdate] = useCan(Action.Update, Subject.ARCash);
  const { activities, isLoading, isError } =
    useArCashReceiptActivity(receiptId);
  const addNote = useAddCashReceiptNote();
  const editNote = useEditCashReceiptNote();
  const deleteNote = useDeleteCashReceiptNote();

  return (
    <CashReceiptActivityFeed
      key={receiptId}
      activities={activities}
      isLoading={isLoading}
      isError={isError}
      canUpdate={canUpdate}
      addPending={addNote.isPending}
      editPending={editNote.isPending}
      deletePending={deleteNote.isPending}
      onAdd={async (body) => {
        await addNote.mutateAsync({ receiptId, body });
      }}
      onEdit={async (activityId, body) => {
        await editNote.mutateAsync({ receiptId, activityId, body });
      }}
      onDelete={async (activityId) => {
        await deleteNote.mutateAsync({ receiptId, activityId });
      }}
    />
  );
}
